import moment from "moment";

/**
 * Checks if a given date is locked based on Markazi Nazim Maliyat lock cut-off day.
 *
 * Rules:
 * 1. If lockDay is not set, 0, or <= 0: locking is disabled -> returns false.
 * 2. Two months prior & earlier (e.g. July or older when today is in Sept): Always locked -> returns true.
 * 3. Immediately preceding month (e.g. August when today is in Sept):
 *    - Locked if today's day-of-month >= lockDay.
 *    - Open if today's day-of-month < lockDay.
 * 4. Current month (and future): Always open -> returns false.
 *
 * @param {string|Date|moment.Moment} date - The transaction date to test
 * @param {number|string} lockDay - The lock day of the month configured in finance_config
 * @returns {boolean} True if locked, false if open
 */
export const isFinanceDateLocked = (date, lockDay) => {
  const day = parseInt(lockDay, 10);
  if (!day || isNaN(day) || day <= 0) {
    return false;
  }

  if (!date) return false;

  const target = moment(date);
  if (!target.isValid()) return false;

  const today = moment();
  const currentMonthStart = today.clone().startOf("month");
  const prevMonthStart = currentMonthStart.clone().subtract(1, "month");
  const twoMonthsPriorStart = currentMonthStart.clone().subtract(2, "months");

  // Current month and immediately preceding month (e.g. August when in Sept) are always open
  if (target.isSameOrAfter(prevMonthStart, "month")) {
    return false;
  }

  // 2 months prior (e.g. July when in Sept): Grace period check
  if (target.isSameOrAfter(twoMonthsPriorStart, "month") && target.isBefore(prevMonthStart, "month")) {
    return today.date() >= day;
  }

  // 3 months prior and earlier are always locked
  return true;
};

/**
 * Calculates the minimum open date allowed for transaction entry/selection.
 *
 * @param {number|string} lockDay - The lock day configured in finance_config
 * @returns {Date|null} Javascript Date for minDate, or null if no lock
 */
export const getFinanceMinOpenDate = (lockDay) => {
  const day = parseInt(lockDay, 10);
  if (!day || isNaN(day) || day <= 0) {
    return null;
  }

  const today = moment();
  if (today.date() < day) {
    // 2 months prior is still open during grace period
    return today.clone().subtract(2, "months").startOf("month").toDate();
  }

  // Preceding month is open
  return today.clone().subtract(1, "month").startOf("month").toDate();
};
